<template>
  <v-row style="height: calc(100% - 64px);">
    <grid
        style="width: 100%; height: 100%;"
        class="ag-theme-material"
        :gridOptions="gridOptions"
        :rowData="rowData"
        :frameworkComponents="frameworkComponents"
        :context="context"
        :loadingOverlayComponent="loadingOverlayComponent"
        :noRowsOverlayComponent="noRowsOverlayComponent"
        :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
        @grid-ready="onGridReady"
        @selection-changed="onSelectionChanged"
    >
    </grid>

    <v-dialog
        v-model="dialog"
        max-width="550"
        scrollable
    >
      <v-card>
        <v-card-title>
          <v-list-item class="px-0">
            <v-list-item-content class="py-0">
              <v-list-item-title class="subtitle-1">
                On target: <span class="font-weight-bold">{{dialogText.gene}}</span>
                <br>
                sgRNA: <span class="font-weight-bold">{{dialogText.sgRNA}}</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-card-title>

        <v-card-text>
          <div class="subtitle-1">Off targets</div>
          <div class="subtitle-2">Very likely</div>
          <v-simple-table v-if="veryLikely.length !== 0" dense>
            <tbody>
            <tr v-for="(val, i) in veryLikely" :key="`key-${i}`">
              <td>{{val.gene}}</td>
              <td v-html="val.sgRNA"></td>
            </tr>
            </tbody>
          </v-simple-table>
          <div v-else>-</div>
          <br>
          <div class="subtitle-2">Likely</div>
          <v-simple-table v-if="likely.length !== 0" dense>
            <tbody>
            <tr v-for="(val, i) in likely" :key="`key-${i}`">
              <td>{{val.gene}}</td>
              <td v-html="val.sgRNA"></td>
            </tr>
            </tbody>
          </v-simple-table>
          <div v-else>-</div>
        </v-card-text>

        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn
              color="primary"
              text
              @click="dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import { AgGridVue } from 'ag-grid-vue';
  import api from '../api';
  import HeaderRenderer from "./GridRenderer/HeaderRenderer.js";
  import TargetRenderer from "./GridRenderer/TargetRenderer.js";
  import LoadingRenderer from "./GridRenderer/LoadingRenderer.js";
  import NoRowsRenderer from "./GridRenderer/NoRowsRenderer";

  export default {
    name: 'Grid',
    components: {
      grid: AgGridVue
    },
    data() {
      return {
        gridOptions: null,
        rowData: null,
        frameworkComponents: null,
        context: null,
        loadingOverlayComponent: null,
        noRowsOverlayComponent: null,
        noRowsOverlayComponentParams: null,
        dialog: false,
        dialogText: {}
      }
    },
    computed: {
      ...mapState([
        'filter',
        'grid'
      ]),
      veryLikely() {
        return this.prepareTargets(this.dialogText.veryLikelyGenes);
      },
      likely() {
        return this.prepareTargets(this.dialogText.likelyGenes);
      }
    },
    created() {
      this.gridOptions = {
        headerHeight: 78,
        suppressCellSelection: true,
        enableCellTextSelection: false,
        rowMultiSelectWithClick: true,
        rowSelection: 'multiple'
      };

      this.gridOptions.defaultColDef = {
        resizable: true,
        sortable: true,
        filter: true,
        minWidth: 90
      };

      this.gridOptions.columnDefs = [
        { headerName: 'Gene name', field: 'gene', minWidth: 140 },
        { headerName: 'sgRNA + NGG', field: 'sgRNA', minWidth: 250 },
        { headerName: 'Position', field: 'position', minWidth: 225 },
        { headerName: 'Exon number', field: 'exon' },
        { headerName: 'Auto-pick "top10"', field: 'autoPick', valueFormatter: params => params.value === 99 ? '-' : params.value },
        { headerName: 'VBC-score', field: 'vbcScore' },
        { headerName: 'Distance start=0 stop=1', field: 'distance' },
        { headerName: 'sgRNA activity 0=bad 1=good', field: 'activity' },
        { headerName: 'Frameshift ratio inDelphi', field: 'frameshift' },
        { headerName: 'Bioscore 0=bad 1=good', field: 'bioscore' },
        { headerName: 'Likely off-targets OT>90', field: 'OT90', cellRenderer: 'targetRenderer' },
        { headerName: 'Cutting site amino acids', field: 'aminoAcids', minWidth: 200 },
        { headerName: 'Very likely off-targets OT>95', field: 'veryLikely', hide: true },
        { headerName: 'Very likely off-target genes', field: 'veryLikelyGenes', hide: true },
        { headerName: 'Likely off-targets OT>90', field: 'likely', hide: true },
        { headerName: 'Likely off-target genes', field: 'likelyGenes', hide: true },
        { headerName: 'Perfect match sites in genome', field: 'perfectMatch', hide: true },
        { headerName: 'Recommended sgRNA-length', field: 'sgRNALength', hide: true },
        { headerName: 'GeCKO cloning FW_oligo', field: 'FWoligo', hide: true },
        { headerName: 'GeCKO cloning RV_oligo', field: 'RVoligo', hide: true },
        { headerName: 'Additional properties', field: 'props', hide: true },
        { headerName: 'Alias gene names', field: 'aliasGenes', hide: true }
      ];

      this.context = { componentParent: this };

      this.frameworkComponents = {
        agColumnHeader: HeaderRenderer,
        targetRenderer: TargetRenderer,
        customLoadingOverlay: LoadingRenderer,
        customNoRowsOverlay: NoRowsRenderer
      };

      this.loadingOverlayComponent = 'customLoadingOverlay';
      this.noRowsOverlayComponent = "customNoRowsOverlay";
      this.noRowsOverlayComponentParams = {
        isSequence: this.filter.sequence
      };

      if (this.filter.sequence) {
        api.getEntries(this.filter.species, null, this.filter.sequenceText)
        .then(res => this.rowData = res.data);
      } else {
        api.getEntries(this.filter.species, this.filter.genes)
        .then(res => this.rowData = res.data);
      }
    },
    mounted() {
      window.addEventListener('resize', this.onResize);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.onResize);
    },
    methods: {
      ...mapActions([
        'initGrid',
        'updateSelectedRows'
      ]),

      onGridReady() {
        this.initGrid(this.gridOptions.api);
        this.grid.api.sizeColumnsToFit();
      },
      onSelectionChanged(event) {
        this.updateSelectedRows(event.api.getSelectedRows());
      },
      onResize() {
        if (this.grid.api) {
          this.grid.api.sizeColumnsToFit();
        }
      },
      openTargetDialog(data) {
        this.dialog = true;
        const { gene, sgRNA, likelyGenes, veryLikelyGenes } = data;
        this.dialogText = {
          ...this.dialogText,
          gene,
          sgRNA,
          likelyGenes,
          veryLikelyGenes
        };
      },
      prepareTargets(val) {
        const targets = [];
        if (val && val !== '0') {
          val.split(',').forEach(target => {
            targets.push({
              gene: target.split('|')[0],
              sgRNA: this.highlight(target.split('|')[1], this.dialogText.sgRNA)
            });
          });
        }
        return targets;
      },
      highlight(newVal, oldVal) {
        let output = '';
        newVal.split('').forEach((val, i) =>
            val !== oldVal.charAt(i) ? output += `<span class='highlight'>${val}</span>` : output += val);
        return output;
      }
    }
  }
</script>
